<script>
  import { onMount, tick } from "svelte";
  import { tourFunctions } from "../tour-functions";
  import { makeEmbedUrl } from "./utils.js";
  const { parseContent } = tourFunctions;

  function fixTitle(title) {
    return title?.replace(/\[.*?\]/g, " ");
  }

  function canShowHotspot(hotspot) {
    const excludeStyles = ["skin_hotspotstyle", "skin_hotspotstyle_balloon"];
    if (excludeStyles.includes(hotspot.style)) return false;
    // make sure there is some content to show
    const hasContent =
      hotspot.popupText ||
      hotspot.popupImage ||
      hotspot.galleryImages?.length > 0 ||
      hotspot.popupVideoEmbed ||
      hotspot.popupSocialUrl;
    return hasContent;
  }

  export let tour;

  let { scenes } = tour;
  let selectedSceneId = scenes[0]?.id || "";
  let isNavigating = false;

  // Select the current scene
  onMount(() => {
    const currentSceneId = tourFunctions.currentScene.getId();
    if (currentSceneId) {
      navigateToScene(currentSceneId);
    }
  });




  async function navigateToScene(id) {
    isNavigating = true;
    selectedSceneId = id;
    const targetSceneElem = document.getElementById("scene-" + id);
    targetSceneElem.scrollIntoView();
    await tick();
    // set focus to the scene title
    targetSceneElem.querySelector(".scene-title").focus();
    isNavigating = false;
  }

  function updateNav(id) {
    selectedSceneId = id;
    // scroll nav item into view if it's not visible
    let navItem = document.getElementById("nav-scene-" + id);
    if (navItem) {
      let nav = document.querySelector(".navigation");
      let navRect = nav.getBoundingClientRect();
      let navItemRect = navItem.getBoundingClientRect();
      if (navItemRect.bottom > navRect.bottom) {
        nav.scrollTop = navItem.offsetTop;
      } else if (navItemRect.top < navRect.top) {
        nav.scrollTop = navItem.offsetTop;
      }
    }
  }

  // Watch content scroll and update navigation
  function observeElement(e, callback) {
    let observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !isNavigating) {
          callback();
        }
      });
    },
    {
      threshold: [0.75],
    });

    observer.observe(e);
  }

  function isVideoIframe(videoUrl) {
    if (/youtube|vimeo|wistia/i.test(videoUrl)) {
      return true;
    } else {
      return false;
    }
  }

</script>

<div class="container">
  <h1 class="tour-title">{tour.accessibilityTitle || tour.name}</h1>
  <div class="content-container">
    <nav class="navigation">
      <h2>Locations</h2>
      <ul>
        {#each scenes.filter((scene) => !scene.hidden) as scene, sceneIndex}
          <li id={`nav-scene-${scene.id}`}>
            <button
              class:active={scene.id === selectedSceneId}
              on:click={() => navigateToScene(scene.id)}
            >
              {sceneIndex+1}. {fixTitle(scene.title)}
            </button>
          </li>
        {/each}
      </ul>
    </nav>

    <main class="content">
      <!-- Intro -->
      {#if tour.accessibilityIntroText}
        <div class="intro">
          {@html parseContent(tour.accessibilityIntroText)}
        </div>
      {/if}

      <!-- Scene Content -->
      {#each scenes.filter((scene) => !scene.hidden) as scene, sceneIndex}
        <div class="scene" id={"scene-" + scene.id}>
          <h2
            tabindex="0"
            class="scene-title"
            use:observeElement={() => updateNav(scene.id)}
          >
            Location: {fixTitle(scene.title)}
          </h2>
          <a href={`#nav-scene-${scene.id}`} class="back-to-nav">Back to Navigation</a>
          <div class="image">
            <img
              src={scene.thumbUrl}
              alt={scene.altText || fixTitle(scene.title)}
              width={1.5 * 250}
              height={1.5 * 156}
            />
          </div>
          <div class="infos">
            {#each scene.hotspots as hotspot}
              {#if canShowHotspot(hotspot)}
                <div class="info" id={"hotspot_" + hotspot.id}>
                  <h3>Hotspot: {fixTitle(hotspot.title)}</h3>

                  {#if hotspot.popupImage}
                    <div class="image">
                      <img
                        class="popup-image"
                        src={hotspot.popupImage.url}
                        width={1.5 * 250}
                        alt={hotspot.popupImage.altText
                          ? hotspot.popupImage.altText
                          : fixTitle(hotspot.title)}
                      />
                    </div>
                  {/if}

                  {#if hotspot.popupVideoEmbed}
                    <div class="popup-video-embed">
                      {#if isVideoIframe(makeEmbedUrl(hotspot.popupVideoEmbed, false))}
                        <iframe
                          class="video-iframe"
                          title="Video"
                          width="100%"
                          src={makeEmbedUrl(hotspot.popupVideoEmbed, false)}
                          allowfullscreen
                          frameborder="0"
                          autoplay="false"
                        />
                      {:else}
                        <!-- svelte-ignore a11y-media-has-caption -->
                        <video controls class="video">
                          <source
                            src={hotspot.popupVideoEmbed}
                            type="video/mp4"
                          />
                        </video>
                      {/if}

                      {#if hotspot.popupVideoEmbedTranscript && hotspot.popupVideoEmbedTranscript?.trim().length > 0}
                        <div class="video-transcript">
                          <h4>Video Transcript</h4>
                          <div>
                            {@html parseContent(
                              hotspot.popupVideoEmbedTranscript,
                            )}
                          </div>
                        </div>
                      {/if}
                    </div>
                  {/if}

                  {#if hotspot.popupSocialUrl}
                    <div class="popup-social-embed">
                      <p>
                        <a href={hotspot.popupSocialUrl} target="_blank" rel="noopener noreferrer">
                          View Instagram content (opens in new tab)
                        </a>
                      </p>
                      {#if hotspot.popupSocialAltText && hotspot.popupSocialAltText.trim().length > 0}
                        <div class="video-transcript">
                          <h4>Description</h4>
                          <div>
                            {@html parseContent(hotspot.popupSocialAltText)}
                          </div>
                        </div>
                      {/if}
                    </div>
                  {/if}

                  {#if hotspot.galleryImages && hotspot.galleryImages.length > 0}
                    <div class="gallery">
                      <h3 class="gallery-title">
                        {fixTitle(hotspot.title)} Gallery
                      </h3>
                      {#each hotspot.galleryImages as image}
                        <div class="image">
                          <img
                            class="gallery-image"
                            src={image.url}
                            alt={image.altText}
                            width={1.5 * 250}
                          />
                        </div>
                      {/each}
                    </div>
                  {/if}
                  <div class="text">
                    <p>{@html parseContent(hotspot.popupText)}</p>
                  </div>
                </div>
              {/if}
            {/each}
          </div>
        </div>
      {/each}
    </main>
  </div>
</div>

<style>
  .tour-title {
    font-family: var(--font-heading), sans-serif;
    text-align: center;
  }
  .content-container {
    display: flex;
    flex-direction: row;
  }

  .navigation {
    flex: 0 0 250px;
    padding: 1rem;
    padding-block-start: 0;
    position: sticky;
    top: 0;
    max-height: 80vh;
    margin: 2em 0;
    margin-block-start: 0;
    overflow: hidden;
    overflow-y: auto;
  }

  .content {
    flex: 1;
    padding: 1rem;
    overflow: auto;
  }

  .navigation ul {
    list-style: none;
    padding: 0;
    color: var(--popup-text-color);
    margin: 0;
  }

  .navigation li {
    margin: 0;
  }

  .navigation button {
    display: inline;
    background: none;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    padding: 0.35em;
    text-align: left;
    color: var(--popup-text-color);
    margin: 3px 0;
    font-size: 1.15em;
    text-decoration: underline;
  }

  .navigation button.active {
    font-weight: bold;
    background: var(--popup-text-color);
    color: var(--popup-bg-color);
  }

  .scene {
    border-bottom: 1px solid rgba(var(--popup-text-color-rgb), 0.25);
    padding: 1rem;
  }

  .scene-title {
    margin-block-end: 0;
  }

  .back-to-nav {
    display: inline-block;
    margin-block-end: 1em;
  }

  .scene h1,
  .scene h2,
  .scene h3 {
    font-family: var(--font-heading), sans-serif;
  }

  p {
    line-height: 1.5;
  }

  img {
    border-radius: 10px;
  }

  .popup-image,
  .gallery-image {
    max-width: 100%;
    height: auto;
    margin: 0.25em 0;
  }

  .video-iframe {
    border-radius: 10px;
    width: 100%;
    max-width: 960px;
    max-height: 50vh;
    aspect-ratio: 16 / 9;
  }

  .button {
    padding: 0.5em 0.75em;
    font-size: 1.25em;
    display: inline-block;
    background: transparent;
    color: var(--popup-text-color);
    border: 2px solid var(--popup-text-color);
    font-weight: bold;
    margin: 0.25em 0;
    border-radius: 10px;
  }

  .button:hover {
    background: var(--popup-text-color);
    color: var(--popup-bg-color);
  }

  .text a {
    font-size: 1em;
    padding: 0;
    color: var(--popup-text-color);
    background: none;
    border: none;
    font-weight: bold;
  }

  ::-webkit-scrollbar {
    width: 3px;
  }
  ::-webkit-scrollbar-thumb {
    background: var(--popup-text-color);
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--popup-text-color);
  }
  /* mobile */
  @media (max-width: 768px) {
    .content-container {
      flex-direction: column;
    }

    .navigation {
      display: none;
    }

    .content {
      flex: 1;
      padding: 0;
    }

    .scene {
      padding: 0;
      margin: 0;
    }

    img {
      max-width: 100%;
      height: auto;
    }
  }
</style>
